import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Wallet } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Opex Source diagnostic (Fix 4).
 *
 * Shows the canonical entity_opex sum the P&L Reconciliation card renders for a
 * scope, alongside recurring-cost coverage, so a zero can be read as genuine
 * (no data) rather than a wiring bug.
 */
var VERDICTS = {
  has_opex: { label: 'Opex present', variant: 'default' },
  no_data: { label: '£0 (no operational cost data entered)', variant: 'outline' },
  property_scoped_only: { label: 'Costs exist but are property-scoped (not entity_opex)', variant: 'destructive' },
  rows_present_zero_sum: { label: 'Rows in scope but sum is 0 (date range / zero amounts)', variant: 'destructive' },
};

export function OpexSourcePanel() {
  var [entities, setEntities] = useState([]);
  var [selectedId, setSelectedId] = useState('br_ltd');
  var [year, setYear] = useState('');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var run = async function () {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.opexSource(selectedId, year || undefined);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var verdict = result ? (VERDICTS[result.verdict] || { label: result.verdict, variant: 'outline' }) : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          Opex Source
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Canonical entity_opex (same source as the P&amp;L Reconciliation card and
          the P&amp;L page) vs recurring-cost coverage, so a zero is legible as
          no-data rather than wrong-wiring.
        </p>
        <div className="flex items-center gap-2">
          <select className="text-sm border rounded px-2 py-1.5 bg-background flex-1" value={selectedId}
            onChange={function (e) { setSelectedId(e.target.value); }}>
            <option value="">Select entity...</option>
            {entities.map(function (ent) { return <option key={ent.id} value={ent.id}>{ent.name}</option>; })}
          </select>
          <input className="text-sm border rounded px-2 py-1.5 bg-background w-24" placeholder="year"
            value={year} onChange={function (e) { setYear(e.target.value); }} />
          <Button size="sm" onClick={run} disabled={loading || !selectedId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{result.entity_name}</span>
              <Badge variant="outline" className="text-xs">{result.source}</Badge>
              <span className="text-xs text-muted-foreground">{result.year}</span>
            </div>

            <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
              <div className="flex justify-between"><span>card Opex (entity_opex)</span><span>{result.card_opex_sum}</span></div>
              <div className="flex justify-between"><span>entity-scope rows (property_id=null)</span><span>{result.entity_scope_row_count}</span></div>
              <div className="flex justify-between"><span>all rows for entity</span><span>{result.all_entity_row_count}</span></div>
              <div className="flex justify-between"><span>pnl_eligible operational rows</span><span>{result.pnl_eligible_operational_rows}</span></div>
              <div className="flex justify-between"><span>has entity_opex component</span><span>{String(result.has_entity_opex_component)}</span></div>
            </div>

            {verdict && (
              <Badge variant={verdict.variant} className={verdict.variant === 'outline' ? 'text-muted-foreground' : ''}>
                {verdict.label}
              </Badge>
            )}

            {result.rows && result.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">cost_type</th>
                      <th className="py-1 pr-2">amount</th>
                      <th className="py-1 pr-2">freq</th>
                      <th className="py-1 pr-2">from</th>
                      <th className="py-1 pr-2">to</th>
                      <th className="py-1 pr-2">bucket</th>
                      <th className="py-1 pr-2">pnl?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map(function (r, i) {
                      return (
                        <tr key={i} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{r.cost_type || '-'}</td>
                          <td className="py-1 pr-2 font-mono">{r.amount}</td>
                          <td className="py-1 pr-2">{r.frequency || '-'}</td>
                          <td className="py-1 pr-2 font-mono">{(r.effective_from || '').slice(0, 7)}</td>
                          <td className="py-1 pr-2 font-mono">{(r.effective_to || '').slice(0, 7) || '-'}</td>
                          <td className="py-1 pr-2">{r.bucket}</td>
                          <td className="py-1 pr-2">{r.pnl_eligible ? 'yes' : 'no'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
